import { ControllerInterface } from "csi/action/Controller";
import FormData from "csi/action/FormData";
import CsiFormData from "csi/action/FormData";
import JsonResponse from "csi/comm/JsonResponse";
import { View } from "csi/def/View";
import { getSlimCardAttrsAsString } from "csi/middleware/card/SlimCardStore";
import { ControllerSaveFunction } from "csi/middleware/common";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, ICache, cnl } from "csi/typings";
import { CheckboxTreeNode, CheckboxTreeStore } from "designSystem/components/checkbox/CheckboxTree";
import { RadioOption } from "designSystem/components/radio/RadioGroup";
import WizardStore from "designSystem/components/wizard/WizardStore";
import { ILocaleProvider } from "designSystem/locale";
import { action, makeObservable, observable } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";

export class MassUpdateWizardStore extends WizardStore {
	/** extension point called to pass "defView" to populate the dojo view */
	protected readonly _defLinkedBOMsView: () => View;

	/** extension point called to handle mass update operation call */
	protected readonly _massUpdateOperation: (createRevision: boolean, urlRevision: cnl, pushAllPlacements: boolean, itemsSelected: cnl[], targetsSelected: cnl[]) => Promise<JsonResponse>;

	/** extension point called to pass Radio Option array (Placements Settings) */
	protected readonly _massUpdateOptions: () => RadioOption<boolean>[];

	/** extension point called to pass Radio Option array (Attributes Settings) */
	protected readonly _attributesSettingsOptions: () => RadioOption<boolean>[];

	/** action bottom message for the firt page of the wizard */
	protected actionMessageRes: string;

	protected readonly cache: ICache = new WorkingCache();
	
	/** Indicates a new revision will be created before making changes on the Size Charts */
	@observable createRevision: boolean;

	disableCreateRevision: boolean = false;

	/** FormData for the wizard */
	readonly formData: CsiFormData;

	/** All Sections */
	private readonly itemsMap: Map<string, CheckboxTreeNode>;

	/** Update option Label*/
	readonly massUpdateOptionName: string;

	/** Placements Settings label for Restore from Style bom and restore from theme bom */
	readonly placementSettingsLabel: string;

	/** Attributes Settings label for Restore from Style bom */
	readonly attributeSettingsLabel: string;

	/** Push all Placements from Theme or Only update what is on Style */
	@observable pushAllOption: boolean;

	@observable overrideAttributes: boolean;

	/** Holds search results check box tree nodes */
	@observable.ref searchedItems: CheckboxTreeNode[];

	/** text for filtering placements by slim card attributes */
	@observable searchText: string;

	/** theme Bom Revision URL */
	readonly revisionURL: cnl;

	/** Select section title Resource */
	selectSectionTitleRes: string;

	/** Second step table viewId */
	tableViewId: string;

	/**  Second step table Node Type */
	tableNodeType: string;

	/**  Second step table sub title resource*/
	tableSubTitleRes: string;

	/** validation Msg Resource before submit the action */
	protected validationMsgRes: string;

	constructor(
		onClose: () => void,
		onSave: ControllerSaveFunction,
		cache: ICache,
		formData: FormData,
		locale: ILocaleProvider,
		stepResourceId: string,
		revisionUrl: cnl,
		itemsMap: Map<string, CheckboxTreeNode>,
	) {
		super(2, stepResourceId, onClose, onSave, locale);
		makeObservable(this);

		this.cache = cache;
		this.revisionURL = revisionUrl;
		this.itemsMap = itemsMap;
		this.pushAllOption = true;
		this.overrideAttributes = false;
		this.createRevision = false;

		this.massUpdateOptionName = this.locale.resource("UpdateOption");
		this.placementSettingsLabel = this.locale.resource("PlacementsSettings");
		this.attributeSettingsLabel = this.locale.resource("AttributesSettings");

		this.formData = formData;
		this.searchedItems = this.items;
	}

	get treeStore(): CheckboxTreeStore {
		return new CheckboxTreeStore(this.searchedItems);
	}

	/** Update option (Placements Settings) for Mass Update Radio Group */
	@action onUpdateOption = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.pushAllOption = value;
	};

	/** Attributes Settings for Mass Update Radio Group */
	@action onUpdateAttributeSettings = (event: ChangeEvent<HTMLInputElement>, value: boolean): void => {
		this.overrideAttributes = value;
	};

	@action onCreateRevision = (value: boolean): void => {
		this.createRevision = value;
	};

	/** Handle the client search by updating searchedItems */
	@action onSearchTextChange = (event: ChangeEvent<HTMLInputElement>): void => {
		const value = event.target.value?.trim()
			.toLocaleLowerCase();
		
		if (value){
			const result = [];
			this.items.forEach((item) => {
				const section = CheckboxTreeNode.createCheckboxTreeNode(item, false);
				item.children.forEach((child) => {	
					if (getSlimCardAttrsAsString(child.uniqueId, this.cache)
						.toLocaleLowerCase()
						.includes(value)) {
						section.addChild(child);
					}
				});
				if (section.children?.length > 0){
					result.push(section);
				}
			});
			this.searchedItems = result;
		} else {
			this.searchedItems = this.items; 
		}
	};

	/** Get options for Mass Update Radio Group */
	get massUpdateOptions(): RadioOption<boolean>[] {
		return this._massUpdateOptions?.() || [];
	}

	/** Get options for Mass Update Radio Group */
	get attributesSettingsOptions(): RadioOption<boolean>[] {
		return this._attributesSettingsOptions?.() || [];
	}

	/** Get items as array */
	get items(): CheckboxTreeNode[] {
		return Array.from(this.itemsMap.values());
	}

	/** Get the sections uniqueId as Array */
	get uniqueIds(): string[] {
		return this.items.map((item) => item.uniqueId);
	}

	/** Get Table View params */
	get viewParams(): Object {
		return {
			bPopup: true,
			defView: this._defLinkedBOMsView(),
		};
	}

	/** Get the number of items selected */
	get itemsSelected(): CheckboxTreeNode[] {
		return this.searchedItems.reduce((memo, item) => memo.concat(item.selectedNodes), []);
	}

	/** Get formData selected Nodes */
	get tableSelectedNodes(): C8Node[] {
		return this.formData.getSelectedNodes();
	}

	/** Get Revision Parent URL */
	get revisionParentUrl(): cnl {
		return this.cache.getPathNode(this.revisionURL, "Child:__Parent__").$URL;
	}

	/* Check if there is at least one item selected. */
	protected onValidate = async (): Promise<boolean> => {
		if (!this.tableSelectedNodes.length) {
			throw new Error(this.locale.resource(this.validationMsgRes));
		}
		return true;
	};

	/* Pass action bottom message for the firt page of the wizard. */
	protected _actionMessage = (): string => {
		if (this.index === 0) {
			return this.locale.resource(this.actionMessageRes, [this.itemsSelected.length]);
		}
		return "";
	};

	/* disable next button if there is not items selected. */
	protected _disablePrimaryButton = (): boolean => {
		if (this.index === 0) {
			return this.itemsSelected.length === 0;
		}
		return false;
	};

	/* Collect the input from the Wizard Forms and sent the request to Mass Update Operation */
	onSavePromise = async (): Promise<JsonResponse> => {
		const { pushAllOption } = this;
		const items = this.itemsSelected.map((p) => p.uniqueId);
		const tableSelectedNodes = this.tableSelectedNodes.map((node) => node.$URL);
		try {
			return this._massUpdateOperation(this.createRevision, this.revisionURL, pushAllOption, items, tableSelectedNodes);
		} catch (err) {
			throw err;
		}
	};
}

export const MassUpdateBOMWizardStoreContext = createContext<MassUpdateWizardStore>(undefined);
export const useMassUpdateBOMWizardStore = (): MassUpdateWizardStore => useContext(MassUpdateBOMWizardStoreContext);

